import {
  isNumber,
  toNumber,
  isNaN,
  isEmpty,
  isUndefined,
  camelCase,
  has,
  isNull
} from 'lodash'
import moment from 'moment'

export const formatDuration = (seconds, human = false) => {
  if (!isNumber(seconds) || seconds === 0) { return '--' }

  let newSeconds = seconds
  const days =  Math.floor(newSeconds / 86400)
  newSeconds -= days * 86400
  const hours = Math.floor(newSeconds / 3600)
  newSeconds -= hours * 3600
  const minutes = Math.floor(newSeconds / 60)
  newSeconds -= minutes * 60

  const symbols = [ 'days', 'hrs', 'mins', 'sec' ]

  let retVal = []

  if (!human) {
    retVal = [ days, hours, minutes, newSeconds ]
      .map(v => formatInt(v))
      .filter(s => s.charAt(0) !== '0' && s.charAt(0) !== '-')
      .join(':')
  } else {
    retVal = [ days, hours, minutes, newSeconds ]
      .map((v, idx) => `${formatInt(v)} ${symbols[idx]}`)
      .filter(s => s.charAt(0) !== '0' && s.charAt(0) !== '-')
      .join(', ')
  }

  return retVal.length > 0
    ? retVal
    : '--'
}

export const formatFloat = value =>
  isNumber(value)
    ? value.toFixed(2).toLocaleString()
    : '--'

export const formatInt = val =>
  isNumber(val)
    ? val.toLocaleString()
    : '--'

export const formatPercent = value =>
  isNumber(value)
    ? value < 1
      ? `${Math.round(value * 100)}%`
      : '100%'
    : '--'

// This function found here: https://stackoverflow.com/a/40724354
const SI_SYMBOL = ["", "k", "M", "G", "T", "P", "E"];
export const abbreviateNumber = num => {
    let n = num

    if (!isNumber(n)) {
      try {
        n = toNumber(n)
      } catch (err) { return '--' }
    }

    if (n === 0 || isNaN(n)) { return '--' }
    // what tier? (determines SI symbol)
    const tier = Math.log10(Math.abs(n)) / 3 | 0

    // if zero, we don't need a suffix
    if(tier == 0) return `${Math.round(n)}`

    // get suffix and determine scale
    const suffix = SI_SYMBOL[tier]
    const scale = Math.pow(10, tier * 3)

    // scale the number
    const scaled = n / scale

    // format number and add suffix
    return `${scaled.toFixed(1)}${suffix}`
}

export const dateRangeLegacyConverter = value => {
  /**
   * Converts a date range value from the new format to the legacy format.
   */
  const m = moment(value, 'YYYY-MM-DD', true)

  if (m.isValid() && value !== '0') { return m.format('MM-DD-YYYY') }

  return moment().add(parseInt(value), 'days').format('MM-DD-YYYY')
}

const formatRangeValue = value => {
  if (isNull(value) || isEmpty(value) || isUndefined(value)) {
    return null
  }

  if (value.eq) { return value.eq }

  const lower = value.gte ||
    value.gt || 0
  const upper = value.lte ||
    value.lt || 0

  return `${abbreviateNumber(lower)} - ${abbreviateNumber(upper)}`
}

const formatDateRangeValue = value => {
  if (isNull(value) || isEmpty(value) || isUndefined(value)) {
    return null
  }

  if (value.eq) { return value.eq }

  let lower = value.gte ||
    value.gt || 0
  let upper = value.lte ||
    value.lt || 0

  if (isNumber(toNumber(lower))) {
    lower = moment().add(toNumber(lower), 'days').format('YYYY-MM-DD')
  }

  if (isNumber(toNumber(upper))) {
    upper = moment().add(toNumber(upper), 'days').format('YYYY-MM-DD')
  }

  return `${lower} - ${upper}`
}

const filterFormatMap = {
  brandIds: value => `Brand Ids: ${value.join(', ')}`,
  collection: value => `Collection: ${value}`,
  completionRate: value => `Completion Rate: ${formatRangeValue(value)}`,
  conversions: value => `Conversions: ${formatRangeValue(value)}`,
  createdDateRange: value => `Created Date: ${formatDateRangeValue(value)}`,
  metricDateRange: value => `Metric Date Range: ${formatDateRangeValue(value)}`,
  customProperties: value => `Properties: ${value.join(', ')}`,
  dataSources: value => `Data Sources: ${value.join(', ')}`,
  dimensionLevelMetrics: value => `Metrics: ${value.join(', ')}`,
  distribution: value => `Distribution: ${value}`,
  domain: value => `Domains: ${value.join(', ')}`,
  duration: value => `Duration: ${formatRangeValue(value)}`,
  humanLanguage: value => `Languages: ${value.join(', ')}`,
  keywords: value => `Keywords: ${value.join(', ')}`,
  queryLevelMetrics: value => `Total Metrics: ${value.join(', ')}`,
  socialActions: value => `Social Actions: ${formatRangeValue(value)}`,
  topics: value => `Topics: ${value.join(', ')}`,
  views: value => `Views: ${formatRangeValue(value)}`,
  wordCount: value => `Word Count: ${formatRangeValue(value)}`,
}

export const humanizeFilter = (key, value) => {
  const preparedKey = camelCase(key)
  if (!has(filterFormatMap, preparedKey)) {
    return null
  }

  if (isNull(value) || isEmpty(value) || isUndefined(value)) {
    return null
  }

  return filterFormatMap[preparedKey](value)
}
